import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, shareReplay, from, map, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { Router } from '@angular/router';
import { 
  createClient, 
  SupabaseClient, 
  AuthChangeEvent as SupabaseAuthChangeEvent, 
  Session as SupabaseSession, 
  User as SupabaseUser,
  SignInWithOAuthCredentials,
  SignInWithSSO
} from '@supabase/supabase-js';

// Interface pour maintenir la compatibilité avec l'ancien code
export interface User {
  id: string;
  email?: string;
  phone?: string;
  created_at: string;
  email_confirmed_at?: string | null;
  phone_confirmed_at?: string | null;
  last_sign_in_at: string;
  app_metadata: { [key: string]: any };
  user_metadata: { [key: string]: any };
  aud: string;
  confirmation_sent_at?: string | null;
  confirmed_at?: string | null;
  email_change_sent_at?: string | null;
  email_change_token?: string | null;
  email_change_confirm_status: number;
  invited_at?: string | null;
  new_email?: string | null;
  new_phone?: string | null;
  phone_change_sent_at?: string | null;
  phone_change_token?: string | null;
  recovery_sent_at?: string | null;
  role: string;
  updated_at: string;
}

export interface Session {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  expires_at: number;
  token_type: string;
  user: User;
}

export type AuthChangeEvent = 
  | 'INITIAL_SESSION'
  | 'SIGNED_IN'
  | 'SIGNED_OUT'
  | 'TOKEN_REFRESHED'
  | 'USER_UPDATED'
  | 'PASSWORD_RECOVERY';

/// <summary>
/// Service for handling Supabase authentication and auth events
/// </summary>
@Injectable({
  providedIn: 'root'
})
export class SupabaseService {
  private supabase: SupabaseClient;
  private _currentUser = new BehaviorSubject<User | null>(null);
  private _session = new BehaviorSubject<Session | null>(null);
  private _initialSessionLoaded = new BehaviorSubject<boolean>(false);

  private handlers: Map<string, { event: AuthChangeEvent, handler: (session: Session | null) => void }> = new Map();

  constructor(private router: Router) {
    // Initialize Supabase client
    this.supabase = createClient(
      environment.supabase.url,
      environment.supabase.anonKey
    );

    // Initialize auth state
    this.initializeAuth();
  }

  /// <summary>
  /// Initializes authentication state and sets up auth state listener
  /// </summary>
  private async initializeAuth(): Promise<void> {
    try {
      console.log('Initializing Supabase auth state...');

      // Get initial session
      const { data: { session }, error } = await this.supabase.auth.getSession();
      
      if (error) {
        console.error('Error getting initial session:', error);
      }

      if (session) {
        this.updateAuthState(session);
        this.triggerAuthHandlers('INITIAL_SESSION', this.convertSupabaseSession(session));
      }

      // Set up auth state change listener
      this.supabase.auth.onAuthStateChange((event, session) => {
        this.handleAuthStateChange(event, session);
      });

      // Mark initial session as loaded
      this._initialSessionLoaded.next(true);
      
    } catch (error) {
      console.error('Error initializing auth:', error);
      this._initialSessionLoaded.next(true);
    }
  }

  /// <summary>
  /// Handles Supabase auth state changes
  /// </summary>
  private handleAuthStateChange(event: SupabaseAuthChangeEvent, session: SupabaseSession | null): void {
    switch (event) {
      case 'INITIAL_SESSION':
        if (session) {
          this.updateAuthState(session);
          this.triggerAuthHandlers('INITIAL_SESSION', this.convertSupabaseSession(session));
        } else {
          this.clearAuthState();
        }
        break;

      case 'SIGNED_IN':
        if (session) {
          this.updateAuthState(session);
          this.triggerAuthHandlers('SIGNED_IN', this.convertSupabaseSession(session));
        }
        break;

      case 'SIGNED_OUT':
        this.clearAuthState();
        this.triggerAuthHandlers('SIGNED_OUT', null);
        break;

      case 'TOKEN_REFRESHED':
        if (session) {
          this.updateAuthState(session);
          this.triggerAuthHandlers('TOKEN_REFRESHED', this.convertSupabaseSession(session));
        }
        break;

      case 'USER_UPDATED':
        if (session) {
          this.updateAuthState(session);
          this.triggerAuthHandlers('USER_UPDATED', this.convertSupabaseSession(session));
        }
        break;

      case 'PASSWORD_RECOVERY':
        this.triggerAuthHandlers('PASSWORD_RECOVERY', session ? this.convertSupabaseSession(session) : null);
        break;
    }
  }

  /// <summary>
  /// Updates the internal auth state with Supabase session
  /// </summary>
  private updateAuthState(session: SupabaseSession): void {
    const user = this.convertSupabaseUser(session.user);
    const convertedSession = this.convertSupabaseSession(session);
    
    this._currentUser.next(user);
    this._session.next(convertedSession);
  }

  /// <summary>
  /// Clears the internal auth state
  /// </summary>
  private clearAuthState(): void {
    this._currentUser.next(null);
    this._session.next(null);
  }

  /// <summary>
  /// Observable to check if the initial session is loaded
  /// </summary>
  get initialSessionLoaded$(): Observable<boolean> {
    return this._initialSessionLoaded.asObservable().pipe(shareReplay(1));
  }

  /// <summary>
  /// Observable to get the user when authenticated
  /// </summary>
  get userAuthenticated$(): Observable<User | null> {
    return new Observable<User | null>((observer) => {
      // First check if we already have a user
      if (this._currentUser.value) {
        observer.next(this._currentUser.value);
        observer.complete();
        return;
      }

      // Wait for initial session to load
      this.initialSessionLoaded$.subscribe(loaded => {
        if (loaded) {
          observer.next(this._currentUser.value);
          observer.complete();
        }
      });
    });
  }

  /// <summary>
  /// Adds an auth state change handler
  /// </summary>
  addAuthStateChangeHandler(event: AuthChangeEvent, handler: (session: Session | null) => void): void {
    this.handlers.set(event + this.uuidv4(), { event, handler });
  }

  /// <summary>
  /// Gets the current user as an observable
  /// </summary>
  get user$(): Observable<User | null> {
    return this._currentUser.asObservable();
  }

  /// <summary>
  /// Gets the current session as an observable
  /// </summary>
  get session$(): Observable<Session | null> {
    return this._session.asObservable();
  }

  /// <summary>
  /// Gets the current user synchronously
  /// </summary>
  get currentUser(): User | null {
    return this._currentUser.value;
  }

  /// <summary>
  /// Gets the current session synchronously
  /// </summary>
  get currentSession(): Session | null {
    return this._session.value;
  }

  /// <summary>
  /// Signs in with Azure SSO using Supabase OAuth
  /// </summary>
  async signInWithAzure(redirectUrl?: string): Promise<void> {
    console.log('Starting Azure SSO login with Supabase...');

    const redirectTo = redirectUrl ? 
      `${window.location.origin}${redirectUrl}` : 
      window.location.origin;

    const credentials: SignInWithSSO = {
      providerId: environment.supabase.providerId,
      options: {
        redirectTo: redirectTo
      }
    };

    // This will get the url to redirect the user to the Azure SSO login page
    const { data, error } = await this.supabase.auth.signInWithSSO(credentials);

    if (error) {
      console.error('Azure SSO error:', error);
      throw error;
    }

    if (data.url) {
      window.location.href = data.url;
    }

  }

  /// <summary>
  /// Alias for signInWithAzure() for backward compatibility
  /// </summary>
  async signInWithSSO(redirectUrl?: string): Promise<void> {
    return this.signInWithAzure(redirectUrl);
  }

  /// <summary>
  /// Signs in with email and password
  /// </summary>
  async signInWithPassword(email: string, password: string): Promise<void> {
    const { error } = await this.supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) {
      console.error('Email/password sign in error:', error);
      throw error;
    }
  }

  /// <summary>
  /// Signs up with email and password
  /// </summary>
  async signUpWithPassword(email: string, password: string, metadata?: object): Promise<void> {
    const { error } = await this.supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata
      }
    });

    if (error) {
      console.error('Email/password sign up error:', error);
      throw error;
    }
  }

  /// <summary>
  /// Signs out the current user
  /// </summary>
  async signOut(): Promise<void> {
    try {
      console.log('Signing out user...');
      const { error } = await this.supabase.auth.signOut();
      
      if (error) {
        console.error('Error signing out:', error);
        throw error;
      }

      console.log('User signed out successfully');

      window.location.href = `${window.location.origin}/login`;
    } catch (error) {
      console.error('Error during sign out:', error);
      throw error;
    }
  }

  /// <summary>
  /// Gets the current access token
  /// </summary>
  async getAccessToken(): Promise<string | null> {
    const { data: { session }, error } = await this.supabase.auth.getSession();

    if (error) {
      console.error('Error getting access token:', error);
      return null;
    }

    return session?.access_token || null;
  }

  /// <summary>
  /// Gets the Supabase client instance
  /// </summary>
  get client(): SupabaseClient {
    return this.supabase;
  }

  /// <summary>
  /// Checks if user is authenticated
  /// </summary>
  get isAuthenticated(): boolean {
    return this._currentUser.value !== null;
  }

  /// <summary>
  /// Manually refreshes the session
  /// </summary>
  async refreshSession(): Promise<void> {
    try {
      const { data, error } = await this.supabase.auth.refreshSession();
      
      if (error) {
        console.error('Error refreshing session:', error);
        throw error;
      }

      console.log('Session refreshed successfully');
    } catch (error) {
      console.error('Error refreshing session:', error);
      throw error;
    }
  }

  /// <summary>
  /// Sends password recovery email
  /// </summary>
  async resetPassword(email: string): Promise<void> {
    const { error } = await this.supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`
    });

    if (error) {
      console.error('Error sending password recovery email:', error);
      throw error;
    }
  }

  /// <summary>
  /// Updates user password
  /// </summary>
  async updatePassword(password: string): Promise<void> {
    const { error } = await this.supabase.auth.updateUser({ password });

    if (error) {
      console.error('Error updating password:', error);
      throw error;
    }
  }

  /// <summary>
  /// Updates user metadata
  /// </summary>
  async updateUserMetadata(metadata: object): Promise<void> {
    const { error } = await this.supabase.auth.updateUser({
      data: metadata
    });

    if (error) {
      console.error('Error updating user metadata:', error);
      throw error;
    }
  }

  /// <summary>
  /// Triggers auth state change handlers
  /// </summary>
  private triggerAuthHandlers(event: AuthChangeEvent, session: Session | null): void {
    for (const [key, handler] of this.handlers) {
      if (handler.event === event) {
        try {
          handler.handler(session);
          // Remove one-time handlers after execution
          if (event === 'INITIAL_SESSION' || event === 'SIGNED_IN') {
            this.handlers.delete(key);
          }
        } catch (error) {
          console.error('Error in auth handler:', error);
        }
      }
    }
  }

  /// <summary>
  /// Converts Supabase user to our User interface
  /// </summary>
  private convertSupabaseUser(supabaseUser: SupabaseUser): User {
    return {
      id: supabaseUser.id,
      email: supabaseUser.email,
      phone: supabaseUser.phone,
      created_at: supabaseUser.created_at,
      email_confirmed_at: supabaseUser.email_confirmed_at,
      phone_confirmed_at: supabaseUser.phone_confirmed_at,
      last_sign_in_at: supabaseUser.last_sign_in_at || supabaseUser.created_at,
      app_metadata: supabaseUser.app_metadata || {},
      user_metadata: supabaseUser.user_metadata || {},
      aud: supabaseUser.aud,
      confirmation_sent_at: supabaseUser.confirmation_sent_at || null,
      confirmed_at: supabaseUser.confirmed_at || null,
      email_change_sent_at: null, // Not available in Supabase user
      email_change_token: null, // Not available in Supabase user
      email_change_confirm_status: 0, // Not available in Supabase user
      invited_at: supabaseUser.invited_at || null,
      new_email: null, // Not available in Supabase user
      new_phone: null, // Not available in Supabase user
      phone_change_sent_at: null, // Not available in Supabase user
      phone_change_token: null, // Not available in Supabase user
      recovery_sent_at: supabaseUser.recovery_sent_at || null,
      role: supabaseUser.role || 'authenticated',
      updated_at: supabaseUser.updated_at || supabaseUser.created_at
    };
  }

  /// <summary>
  /// Converts Supabase session to our Session interface
  /// </summary>
  private convertSupabaseSession(supabaseSession: SupabaseSession): Session {
    return {
      access_token: supabaseSession.access_token,
      refresh_token: supabaseSession.refresh_token,
      expires_in: supabaseSession.expires_in || 3600,
      expires_at: supabaseSession.expires_at || Math.floor(Date.now() / 1000) + 3600,
      token_type: supabaseSession.token_type || 'bearer',
      user: this.convertSupabaseUser(supabaseSession.user)
    };
  }

  /// <summary>
  /// Generates a UUID v4
  /// </summary>
  private uuidv4(): string {
    return crypto.randomUUID();
  }
}